import { Router } from '@angular/router';
import { OnInit } from '@angular/core';
import { Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Component } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Validators } from '@angular/forms';
import { FormControl } from '@angular/forms';

import { User } from 'src/app/model/settings/user.model';
import { Partner } from 'src/app/model/settings/partner.model';
import { AuthService } from 'src/app/service/auth/auth.service';
import { environment } from 'src/environments/environment';
import { AlertService } from 'src/app/service/alert.service';
import { SessionService } from 'src/app/service/auth/session.service';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';
import { initPendo } from 'src/app/util/pendo.util';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {

  public capsOn = false;
  public loginForm!: FormGroup<{ email: FormControl<string>; password: FormControl<string> }>;
  public forgotForm!: FormGroup<{ email: FormControl<string> }>;
  public remember: boolean = false;
  public recover: boolean = false;
  public currentYr = new Date().getFullYear();

  constructor(private router: Router,
              private authService: AuthService,
              private sessionService: SessionService,
              private alertService: AlertService,
              private analytics: AnalyticsService,
              @Inject(DOCUMENT) private document: Document) { }

  ngOnInit(): void {
    this.sessionService.snackBar.dismiss();
    let username: string = "";
    const remember = localStorage.getItem("portal-rememberUser");

    if (remember){
      username = remember;
      this.remember = true;
    }

    this.loginForm = new FormGroup({email: new FormControl(username, { nonNullable: true, validators: [Validators.email, Validators.required] }),
                                    password: new FormControl("", { nonNullable: true, validators: [Validators.required] })});

    this.forgotForm = new FormGroup({email: new FormControl(username, { nonNullable: true, validators: [Validators.required, Validators.email] })});
  }

  onLogin(): void {

    this.authService
        .login(this.loginForm.get("email")?.value,
               this.loginForm.get("password")?.value)
        .subscribe(res => {

          const user = new User().load(res);
          const partner = res.partner ? new Partner().load(res.partner) : undefined;

          // Non-secret marker only — the BFF deliberately does not return the
          // session ID (it's the cookie value; persisting it in sessionStorage
          // would undo the HttpOnly design). isValid()/refresh just need truthiness.
          this.sessionService.start(user,
                                    partner,
                                    'authenticated');
          // Track after start() so identify + super properties are set first.
          this.analytics.track('Login Succeeded');
          initPendo(user, partner);

          if (this.remember){
            localStorage.setItem("portal-rememberUser", user.email);
          } else {
            localStorage.removeItem("portal-rememberUser");
          }

          if (environment.version) {
            this.router
                .navigate(['home'],
                          {queryParams: { v: environment.version },
            });
          } else {
            this.router
                .navigateByUrl('home');
          }

        }, err => {
          let message;

          console.error(err);

          // Anonymous (no identify yet) — status only, never the email.
          this.analytics.track('Login Failed', { status: err.status });

          switch(err.status){
            case 0:
              message = 'Running system maintenance, retry later';
              break;

            case 401:
              message = 'Unable to authenticate, verify your email and password';
              break;

            default:
              message = 'Unable to login, retry later';
          }

          this.alertService
              .information(message);
        });
  }

  onSignup(): void {
    this.document.location.href = 'https://www.tonicpos.com';
    this.sessionService.destroySession();
  }

  onRemember(value: any): void {
    this.remember = value.checked;
  }

  public onReset(): void {
    this.authService
        .forgot(this.forgotForm.get('email')?.value)
        .subscribe(()=>{
          this.analytics.track('Password Reset Requested');
          this.alertService
              .information('Reset info sent to your email');
          this.recover = false;
        }, (err)=>{
          this.alertService
              .information('Unable to reset, retry later');
        }
    );
  }

  public onCapsLock(evt: any): void {
    this.capsOn = evt;
  }
}
